import type { DataManagementService } from "../services/dataManagement";
import type {
  AccFolderEntry,
  AccHub,
  AccProject,
} from "../services/dataManagement/types";
import type { PackageFileRef } from "./matchSubmissionDocs";
import { isSubmissionFileExtension } from "./submissionFileTypes";

const MAX_FOLDERS = 80;

export async function scanAccProjectPackage(
  dm: DataManagementService,
  hub: AccHub,
  project: AccProject,
  options: {
    rootFolderId?: string;
    rootFolderPath?: string;
  } = {}
): Promise<{ files: PackageFileRef[]; foldersVisited: number }> {
  const files: PackageFileRef[] = [];
  const queue: { folderId: string; path: string }[] = [];
  let foldersVisited = 0;

  if (options.rootFolderId) {
    queue.push({
      folderId: options.rootFolderId,
      path: options.rootFolderPath ?? "Submission folder",
    });
  } else {
    const top = await dm.getProjectTopFolders(hub.id, project.id, hub.region);
    for (const entry of top) {
      if (entry.objectType === "folders") {
        queue.push({ folderId: entry.id, path: entry.name });
      }
    }
  }

  while (queue.length > 0 && foldersVisited < MAX_FOLDERS) {
    const current = queue.shift();
    if (!current) break;
    foldersVisited += 1;

    const contents = await dm.getFolderContents(
      project.id,
      current.folderId,
      hub.region
    );

    for (const entry of contents) {
      if (entry.objectType === "folders") {
        queue.push({
          folderId: entry.id,
          path: `${current.path} / ${entry.name}`,
        });
        continue;
      }
      if (!isSubmissionFileExtension(entry.extension)) continue;
      files.push({
        itemId: entry.id,
        fileName: entry.name,
        extension: entry.extension ?? "",
        folderPath: current.path,
      });
    }
  }

  return { files, foldersVisited };
}

/** Use the folder the user is browsing as the dedicated submission folder. */
export function folderEntryFromBrowse(
  activeFolderId: string | null,
  crumbs: { id: string; name: string }[]
): AccFolderEntry | null {
  if (!activeFolderId) return null;
  const name = crumbs.map(c => c.name).join(" / ") || "Folder";
  return { id: activeFolderId, name, objectType: "folders" };
}
